<!--
  @component MonacoEditor — a thin Svelte 5 wrapper around monaco-editor.
  tier:    consumer leaf (Code screen only — Monaco is a heavy client dep we
           keep out of global so admin never bundles it)
  shape:   two-way `value` binding · language derived from the file path ·
           a Slate-Ink theme read live from the app's CSS tokens (so it
           tracks light/dark/high-contrast). Disposes the editor + model on
           unmount. Workers load via Vite's native `?worker` imports.
-->
<script lang="ts" module>
  import editorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker";
  import jsonWorker from "monaco-editor/esm/vs/language/json/json.worker?worker";
  import cssWorker from "monaco-editor/esm/vs/language/css/css.worker?worker";
  import htmlWorker from "monaco-editor/esm/vs/language/html/html.worker?worker";
  import tsWorker from "monaco-editor/esm/vs/language/typescript/ts.worker?worker";

  // MonacoEnvironment must be set ONCE, before any editor is created. Vite
  // bundles each `?worker` import as its own worker chunk.
  const g = self as unknown as { MonacoEnvironment?: unknown };
  if (!g.MonacoEnvironment) {
    g.MonacoEnvironment = {
      getWorker(_id: string, label: string): Worker {
        if (label === "json") return new jsonWorker();
        if (label === "css" || label === "scss" || label === "less") return new cssWorker();
        if (label === "html" || label === "handlebars" || label === "razor") return new htmlWorker();
        if (label === "typescript" || label === "javascript") return new tsWorker();
        return new editorWorker();
      },
    };
  }

  /** Map a workspace file path → a monaco language id. */
  export function languageForPath(path: string): string {
    const dot = path.lastIndexOf(".");
    const ext = dot >= 0 ? path.slice(dot + 1).toLowerCase() : "";
    const base = path.slice(path.lastIndexOf("/") + 1).toLowerCase();
    if (base === "dockerfile") return "dockerfile";
    const map: Record<string, string> = {
      ts: "typescript", tsx: "typescript", mts: "typescript", cts: "typescript",
      js: "javascript", jsx: "javascript", mjs: "javascript", cjs: "javascript",
      json: "json", jsonc: "json",
      css: "css", scss: "scss", less: "less",
      html: "html", htm: "html", svelte: "html", vue: "html",
      md: "markdown", markdown: "markdown",
      yaml: "yaml", yml: "yaml", toml: "ini", ini: "ini", env: "ini",
      sh: "shell", bash: "shell", zsh: "shell",
      py: "python", rs: "rust", go: "go", sql: "sql", xml: "xml", graphql: "graphql",
    };
    return map[ext] ?? "plaintext";
  }
</script>

<script lang="ts">
  import * as monaco from "monaco-editor";
  import { onMount } from "svelte";

  interface Props {
    /** Editor contents (two-way). */
    value: string;
    /** Monaco language id (e.g. from languageForPath). */
    language?: string;
    readOnly?: boolean;
  }
  let { value = $bindable(), language = "plaintext", readOnly = false }: Props = $props();

  let host: HTMLDivElement;
  let editor: monaco.editor.IStandaloneCodeEditor | undefined;
  // Guard so the model→value sync doesn't fight the value→model sync.
  let applyingExternal = false;

  // ── Slate-Ink theme, read from the live CSS tokens ───────────────
  // Monaco needs hex; our text tokens are rgba() over the surface. We
  // composite to hex so the editor matches whatever theme is active.
  function compositeHex(color: string, overHex: string): string {
    const c = color.trim();
    if (c.startsWith("#")) {
      if (c.length === 7) return c;
      if (c.length === 4) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
      if (c.length === 9) color = c; // fall through to rgba compositing below via parse
    }
    const m = c.match(/rgba?\(([^)]+)\)/i);
    const over = hexToRgb(overHex);
    if (!m || !m[1]) return overHex;
    const parts = m[1].split(",").map((s) => parseFloat(s.trim()));
    const r = parts[0] ?? 0, gg = parts[1] ?? 0, b = parts[2] ?? 0, a = parts[3] ?? 1;
    const mix = (fg: number, bg: number) => Math.round(bg * (1 - a) + fg * a);
    return rgbToHex(mix(r, over.r), mix(gg, over.g), mix(b, over.b));
  }
  function hexToRgb(h: string): { r: number; g: number; b: number } {
    const s = h.replace("#", "");
    return { r: parseInt(s.slice(0, 2), 16) || 0, g: parseInt(s.slice(2, 4), 16) || 0, b: parseInt(s.slice(4, 6), 16) || 0 };
  }
  function rgbToHex(r: number, g: number, b: number): string {
    const h = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`;
  }
  function luminance(hex: string): number {
    const { r, g, b } = hexToRgb(hex);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }

  const THEME = "slate-ink";
  function defineTheme(): void {
    const cs = getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
    const bg = v("--surface-base", "#15151b");
    const sunken = v("--surface-sunken", bg);
    const fg = compositeHex(v("--text-primary", "#ffffff"), bg);
    const dim = compositeHex(v("--text-secondary", "#9a9aa5"), bg);
    const ghost = compositeHex(v("--text-tertiary", "#6a6a75"), bg);
    const border = compositeHex(v("--border", "#2a2a32"), bg);
    const accent = (v("--accent", "#60a5fa").match(/#[0-9a-f]{6}/i)?.[0]) ?? "#60a5fa";
    monaco.editor.defineTheme(THEME, {
      base: luminance(bg) > 0.5 ? "vs" : "vs-dark",
      inherit: true,
      rules: [{ token: "comment", foreground: ghost.replace("#", "") }],
      colors: {
        "editor.background": bg,
        "editor.foreground": fg,
        "editorGutter.background": bg,
        "editorLineNumber.foreground": ghost,
        "editorLineNumber.activeForeground": dim,
        "editor.lineHighlightBackground": sunken,
        "editor.lineHighlightBorder": "#00000000",
        "editorCursor.foreground": accent,
        "editor.selectionBackground": `${accent}33`,
        "editorIndentGuide.background1": border,
        "editorWidget.background": sunken,
        "editorWidget.border": border,
        "input.background": sunken,
        "scrollbarSlider.background": `${border}`,
      },
    });
  }

  onMount(() => {
    defineTheme();
    editor = monaco.editor.create(host, {
      value,
      language,
      theme: THEME,
      readOnly,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: 12.5,
      lineHeight: 20,
      fontFamily: "'SF Mono', 'Fira Code', ui-monospace, monospace",
      scrollBeyondLastLine: false,
      renderLineHighlight: "line",
      smoothScrolling: true,
      tabSize: 2,
      padding: { top: 10, bottom: 10 },
      scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
    });
    const sub = editor.onDidChangeModelContent(() => {
      if (applyingExternal || !editor) return;
      value = editor.getValue();
    });
    return () => {
      sub.dispose();
      editor?.getModel()?.dispose();
      editor?.dispose();
      editor = undefined;
    };
  });

  // value → editor (opening a different file / external reset).
  $effect(() => {
    const v = value;
    if (editor && editor.getValue() !== v) {
      applyingExternal = true;
      editor.setValue(v);
      applyingExternal = false;
    }
  });

  // language → editor model.
  $effect(() => {
    const lang = language;
    const model = editor?.getModel();
    if (model) monaco.editor.setModelLanguage(model, lang);
  });

  // readOnly → editor option.
  $effect(() => {
    editor?.updateOptions({ readOnly });
  });
</script>

<div class="monaco-host" bind:this={host}></div>

<style>
  .monaco-host {
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
</style>
